import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useCarPlateApi } from "../../http/carPlates/api";
import { UpsertCarPlate } from "../../http/carPlates/types";
import ModalDialog from "../ui/ModalDialog";
import CarPlateForm, { CarPlateFormBody } from "./CarPlateForm";

type EditCarPlateDialogProps = {
  carPlateId: string;
  onClose: (success?: boolean) => void;
  carId?: string;
};

const EditCarPlateDialog: FC<EditCarPlateDialogProps> = ({ carPlateId, carId, onClose }: EditCarPlateDialogProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const carPlatesApi = useCarPlateApi();

  const carPlateSwr = carPlatesApi.getAdminCarPlate(carPlateId, aborter.current.signal);
  const [carPlate, isInitialized] = useDataInitializer(carPlateSwr.data, carPlateSwr.error);

  useEffect(() => {
    carPlateSwr.trigger();
  }, [carPlateId]);

  useEffect(() => {
    carPlateSwr.error && dispatch(showErrorNotification(i18n, carPlateSwr.error));
  }, [carPlateSwr.error]);

  const onSubmit = async (formData: CarPlateFormBody): Promise<void> => {
    if (carPlate?.id) {
      const body: UpsertCarPlate = {
        carId: carId ?? formData.car.id,
        nr: formData.nr,
        startDate: formData.startDate,
        endDate: formData.endDate,
        currentVersionNumber: carPlate?.versionNumber,
      };

      setIsSubmitting(true);
      try {
        await carPlatesApi.updateAdminCarPlate(carPlate.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose(true);
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.carPlates.edit}>
      <CarPlateForm
        onSubmit={onSubmit}
        isDisabled={!carPlate?.id || !userSettings.permissions.carPlates.update || isSubmitting}
        isLoading={!isInitialized}
        carPlate={carPlate}
        allowSelectCar={!carId}
      />
    </ModalDialog>
  );
};

export default EditCarPlateDialog;
